import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import '../styles/PremiumDashboard.css';

const ApprovalList = () => {
    const [pendingLinks, setPendingLinks] = useState([]);
    const [pendingLPs, setPendingLPs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [message, setMessage] = useState('');

    const fetchPendingItems = async () => {
        try {
            const [linksRes, lpsRes] = await Promise.all([
                api.get('/op/approvals/links'),
                api.get('/op/approvals/landing-pages')
            ]);

            if (linksRes.data.success) setPendingLinks(linksRes.data.links);
            if (lpsRes.data.success) setPendingLPs(lpsRes.data.landingPages);

        } catch (error) {
            console.error('Error fetching pending items:', error);
            setMessage('Eroare la încărcarea cererilor.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingItems();
    }, []);

    const handleLinkAction = async (id, action) => {
        setProcessing(id);
        setMessage('');
        try {
            const response = await api.put(`/op/approvals/link/${id}`, { status: action });
            if (response.data.success) {
                setMessage(response.data.message);
                fetchPendingItems();
            }
        } catch (error) {
            setMessage(error.response?.data?.error || 'Eroare la procesare.');
        } finally {
            setProcessing(null);
        }
    };

    const handleLpAction = async (id, action) => {
        setProcessing(`lp-${id}`);
        setMessage('');
        try {
            const endpoint = `/op/landing-pages/${id}`;

            const updateData = action === 'activ'
                ? { activ: true, status: 'activ' }
                : { activ: false, status: 'respins' };

            const response = await api.put(endpoint, updateData);

            if (response.data.success) {
                setMessage(action === 'activ' ? 'Landing Page aprobat!' : 'Landing Page respins.');
                fetchPendingItems();
            }
        } catch (error) {
            setMessage(error.response?.data?.error || 'Eroare la procesare.');
        } finally {
            setProcessing(null);
        }
    };

    if (loading) return <div className="text-gray-500 text-center py-4">Se încarcă cererile...</div>;

    return (
        <div className="space-y-6">
            <div className="section-header" style={{ marginBottom: 0 }}>
                <span className="section-title">Aprobări</span>
                <span className="section-line" />
            </div>
            {message && (
                <div className={`p-4 rounded-md ${message.includes('Eroare') ? 'bg-red-500/15 text-red-400 border border-red-500/30' : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'}`}>
                    {message}
                </div>
            )}

            {/* Custom LP Approvals */}
            <div className="agent-leads-table-wrap overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--border)] bg-[var(--surface2)]">
                    <h3 className="text-lg font-medium text-[var(--text)]">Aprobări Landing Pages Custom</h3>
                </div>
                {pendingLPs.length === 0 ? (
                    <div className="p-6 text-center text-[var(--text-muted)] italic">Nu există Landing Pages în așteptare.</div>
                ) : (
                    <ul className="divide-y divide-[var(--border)]">
                        {pendingLPs.map((lp) => (
                            <li key={lp.id} className="p-6 hover:bg-[var(--bar-bg)] transition-colors">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0">
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-medium text-[var(--text)]">{lp.titlu}</h4>
                                        <p className="text-sm text-[var(--text-muted)] mt-1">Creat de: <span className="font-semibold">{lp.agent?.numeComplet || 'Agent Necunoscut'}</span></p>
                                        <p className="text-xs text-[var(--text-muted)] mt-1">Slug: /lp/{lp.slug}</p>
                                        <div className="mt-2 text-sm text-[var(--text-soft)] line-clamp-2">{lp.descriere}</div>
                                        <a href={`/lp/${lp.slug}?preview=true`} target="_blank" rel="noreferrer" className="text-[#8ba4fc] text-xs hover:underline mt-2 inline-block">
                                            Previzualizează LP ↗
                                        </a>
                                    </div>
                                    <div className="ml-4 flex-shrink-0 flex space-x-3">
                                        <button
                                            onClick={() => handleLpAction(lp.id, 'activ')}
                                            disabled={processing === `lp-${lp.id}`}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                                        >
                                            {processing === `lp-${lp.id}` ? '...' : 'Aprobă'}
                                        </button>
                                        <button
                                            onClick={() => handleLpAction(lp.id, 'respins')}
                                            disabled={processing === `lp-${lp.id}`}
                                            className="inline-flex items-center px-3 py-2 border border-[var(--border)] text-sm font-medium rounded-lg text-[var(--text)] bg-[var(--surface2)] hover:bg-[var(--bar-bg)] disabled:opacity-50"
                                        >
                                            Respinge
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Link Approvals */}
            <div className="agent-leads-table-wrap overflow-hidden mt-6">
                <div className="px-6 py-4 border-b border-[var(--border)] bg-[var(--surface2)]">
                    <h3 className="text-lg font-medium text-[var(--text)]">Aprobări Link-uri</h3>
                </div>
                {pendingLinks.length === 0 ? (
                    <div className="p-6 text-center text-[var(--text-muted)] italic">Nu există cereri de link în așteptare.</div>
                ) : (
                    <ul className="divide-y divide-[var(--border)]">
                        {pendingLinks.map((link) => (
                            <li key={link.id} className="p-6 hover:bg-[var(--bar-bg)] transition-colors">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/30">
                                                Link Request
                                            </span>
                                            <span className="text-sm text-[var(--text-muted)]">
                                                {new Date(link.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm font-medium text-[var(--text)]">
                                            {link.agent?.numeComplet} vrea să promoveze &quot;{link.landingPage?.titlu}&quot;
                                        </p>
                                    </div>
                                    <div className="ml-4 flex-shrink-0 flex space-x-3">
                                        <button
                                            onClick={() => handleLinkAction(link.id, 'activ')}
                                            disabled={processing === link.id}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                                        >
                                            {processing === link.id ? '...' : 'Aprobă'}
                                        </button>
                                        <button
                                            onClick={() => handleLinkAction(link.id, 'rejected')}
                                            disabled={processing === link.id}
                                            className="inline-flex items-center px-3 py-2 border border-[var(--border)] text-sm font-medium rounded-lg text-[var(--text)] bg-[var(--surface2)] hover:bg-[var(--bar-bg)] disabled:opacity-50"
                                        >
                                            Respinge
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default ApprovalList;
